/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component,
  forwardRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  QueryList, ElementRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { Textarea } from 'primeng/textarea';
import { TranslationService } from '../../common/translation.service';

/**
 * Component for our aero-textarea field.
 */
@Component({
  selector: 'aero-textarea',
  templateUrl: './aero-textarea.component.html',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AeroTextareaComponent),
    multi: true,
  }],
  standalone: false,
})
export class AeroTextareaComponent implements ControlValueAccessor, OnInit {
  /**
   * Id for the text area field.
   */
  @Input() public id : string;

  /**
   * Required validation for the text area field.
   */
  @Input() public required? : boolean = false;

  /**
   * Placeholder for the text area field.
   */
  @Input() public placeholder? : string = '';

  /**
   * Disable the state for the text area field by setting the value as true
   */
  @Input() public disabled? : boolean = false;

  /**
   * Label for the text area field.
   */
  @Input() public label : string = '';

  /**
   * Set the error message for the text area field.
   */
  @Input() public errorMessage : string = '';

  /**
   * Autosize for the text area field set the value as true.
   */
  @Input() public autoResize : boolean = false;

  /**
   * Set the rows for the text area field.
   */
  @Input() public rows : number = 5;

  /**
   * Set the columns for the text area field.
   */
  @Input() public columns : number = 80;

  /**
   * Maxlength for the text area field.
   */
  @Input() public maxlength : number = 160;

  /**
   * Id for the text area field.
   */
  @Input() public name? : string;

  /**
   * Aria label for the text area field.
   */
  @Input() public ariaLabel : string;

  /**
   * Apply autofocus to input using bulbAutofocus directive.
   */
  @Input() public autofocus = false;

  /**
   * Event emitter for the content change in the textarea field.
   */
  @Output() public contentChangeHandler = new EventEmitter();

  /**
   * Textarea element rendered on DOM.
   */
  @ViewChildren(Textarea) textAreaList: QueryList<Textarea>;

  /**
   * Reference to the DOM textarea element.
   */
  @ViewChild('textarea') aeroTextareaNgModel : NgModel;

  /**
   * Set the value for the text area field.
   */
  private value : string;

  /**
   * Update the resize of the textarea.
   */
  private isResizeInitialized: boolean = false;

  /**
   * Default Constructor.
   */
  constructor(
    private elementRef: ElementRef,
    private $translate : TranslationService,
  ) {
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    // Translate the default error message
    this.errorMessage = this.errorMessage || this.$translate.translateInstant('validation_error_required');
  }

  /**
   * Method to emit the content change on the textarea.
   *
   * @param event for the input
   */
  public onContentChange(event) {
    this.contentChangeHandler.emit(event);
  }

  /**
   * Get the value of the textarea.
   */
  get textareaValue(): any {
    if (this.autoResize && !this.isResizeInitialized && this.elementRef.nativeElement.offsetHeight > 0) {
      this.isResizeInitialized = true;
      this.resize();
    }
    return this.value;
  }

  /**
   * Set the value of the textarea.
   *
   * @param newValue of the textarea field.
   */
  set textareaValue(newValue: any) {
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(newValue);
    }
  }

  /**
   * Callback function to register the UI value changes.
   */
  onChange: any = () => { };

  /**
   * Callback function to register on element touch.
   */
  onTouched: any = () => { };

  /**
   * Set the touched on the blur event.
   */
  onBlur() {
    this.onTouched();
  }

  /**
   * Method to set the value.
   *
   * @param updateValue of the textarea field.
   */
  writeValue(updateValue: any) {
    if (updateValue !== this.value) {
      this.value = updateValue;
    }
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the textarea field.
   */
  registerOnChange(updateValue: any) {
    this.onChange = updateValue;
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the textarea field.
   */
  registerOnTouched(updateValue: any) {
    this.onTouched = updateValue;
  }

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.onChange(this.value);
  }

  /**
   * Method called by the parent component to reset this field.
   */
  resetField() {
    const fieldControl = this.aeroTextareaNgModel.control;
    fieldControl.setErrors(null);
    fieldControl.markAsPristine();
    fieldControl.markAsUntouched();
  }

  /**
   * Resize the textarea.
   */
  private resize() {
    if (this.textAreaList) {
      this.textAreaList.first.resize();
    }
  }
}
